import { useEffect, useMemo, useRef, useState } from 'react';

import type { AgentProgressEvent } from '~/components/AiChat/AgentTimeline/types';
import { useRealTimeToken } from '~/components/AiChat/hooks/useRealTimeToken';
import { useRealTimeUpdates } from '~/components/AiChat/hooks/useRealTimeUpdates';
import { useDelayedRefresh } from '~/components/ProgressTimeline/useDelayedRefresh';
import { documentProgressFirestoreService } from '~/services/firestore/firestoreService';

import { toDocumentProgressByKey } from './documentProgressReducer';
import type { DocumentProgress } from './documentProgressTypes';
import { mergeLatestPlans } from './documentProgressUtils';

const EVENT_LIMIT = 300;
// The terminal event lands once CMS holds the result, but a deep-analysis outcome still travels one
// queue hop after it; refetch at once, then twice more to catch that straggler.
const REFRESH_DELAYS_MS = [0, 2000, 5000];

/**
 * Live processing rows for every document on a claim, keyed by the external id the AI service was
 * sent — each row's `ai_progress_key` (`document_<id>` for a plain upload).
 *
 * One claim-scoped subscription for the whole tab. When a document's run ends, `onDocumentFinished`
 * fires so the table refetches the type, summary and status the run produced.
 */
export const useDocumentProgress = ({
  claimId,
  enabled,
  onDocumentFinished,
}: {
  claimId: number;
  enabled: boolean;
  onDocumentFinished?: () => void;
}): Map<string, DocumentProgress> => {
  const issueTokenResponse = useRealTimeToken(enabled ? `/api/v1/claims/${claimId}/document-progress/token` : null);
  // Kept past the event window, so a run whose `plan` the window evicted still reduces to its rows.
  const [latestPlans, setLatestPlans] = useState<Map<string, AgentProgressEvent>>(() => new Map());

  useEffect(() => {
    setLatestPlans(new Map());
  }, [claimId, enabled]);

  const { events, hasReceivedInitialSnapshot } = useRealTimeUpdates({
    issueTokenResponse,
    enabled: enabled && Boolean(issueTokenResponse),
    service: documentProgressFirestoreService,
    eventLimit: EVENT_LIMIT,
  });

  useEffect(() => {
    setLatestPlans((previous) => mergeLatestPlans(events, previous));
  }, [events]);

  const progressByKey = useMemo(() => toDocumentProgressByKey(events, latestPlans), [events, latestPlans]);

  const { schedule } = useDelayedRefresh(onDocumentFinished, REFRESH_DELAYS_MS);
  const runningKeys = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!hasReceivedInitialSnapshot) return;
    let finished = false;
    progressByKey.forEach((progress, key) => {
      if (progress.isRunning) runningKeys.current.add(key);
      else if (runningKeys.current.delete(key)) finished = true;
    });
    if (finished) schedule();
  }, [progressByKey, hasReceivedInitialSnapshot, schedule]);

  return progressByKey;
};

export default useDocumentProgress;
